import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Search, X, Filter, ArrowUp, ArrowDown, Calendar, CheckCircle, CreditCard } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Subscription } from '@/types';
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SubscriptionWithRelations extends Subscription {
  subscription_customers: {
    id: string;
    customer_role: 'owner' | 'participant' | 'billing_contact';
    customer: {
      id: string;
      name: string;
    };
  }[];
  parent_subscription?: {
    id: string;
    display_id: string;
  };
}

const Step2Subscription = () => {
  console.log('🔄 STEP2: Component mounted/rendered at:', window.location.pathname);
  
  const navigate = useNavigate();
  const { state, setState } = useClassWizard();
  
  console.log('🔄 STEP2: Current wizard state:', state);
  console.log('🔄 STEP2: Selected customer:', state.selectedCustomer);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<SubscriptionWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    console.log('🔄 STEP2: Validation check - selectedCustomer:', state.selectedCustomer);
    if (!state.selectedCustomer) {
      console.log('🚨 STEP2: No customer selected, redirecting to step1');
      toast.error('Selecione um cliente primeiro');
      navigate('/class-wizard/step1');
      return;
    }
    console.log('✅ STEP2: Customer validation passed, fetching subscriptions');
    fetchSubscriptions();
  }, [state.selectedCustomer]);

  useEffect(() => {
    applyFilters();
  }, [subscriptions, searchTerm, customerFilter, relationshipFilter, sortOrder]);

  const fetchSubscriptions = async () => {
    if (!state.selectedCustomer) {
      console.log('🚨 STEP2: No selectedCustomer, skipping fetchSubscriptions');
      return;
    }
    
    console.log('🔄 STEP2: Fetching subscriptions for customer:', state.selectedCustomer.id);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          subscription_customers!inner (
            id,
            customer_role,
            customer:customer_id (
              id,
              name
            )
          ),
          parent_subscription:parent_subscription_id (
            id,
            display_id
          )
        `)
        .eq('state', 'active')
        .eq('subscription_customers.customer_id', state.selectedCustomer.id);

      if (error) {
        console.error('🚨 STEP2: Supabase error:', error);
        throw error;
      }
      
      console.log('✅ STEP2: Fetched subscriptions:', data);
      setSubscriptions(data || []);
      setFilteredSubscriptions(data || []);
    } catch (error: any) {
      console.error('🚨 STEP2: Error fetching subscriptions:', error);
      toast.error('Erro ao carregar assinaturas', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...subscriptions];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => 
        sub.id.toLowerCase().includes(term) ||
        sub.display_id?.toLowerCase().includes(term) ||
        sub.subscription_customers.some(sc => 
          sc.customer.name.toLowerCase().includes(term) ||
          sc.customer_role.toLowerCase().includes(term)
        )
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(sub => 
        sub.subscription_customers.some(sc => 
          sc.customer.name.toLowerCase().includes(customerFilter.toLowerCase())
        )
      );
    }

    if (relationshipFilter) {
      filtered = filtered.filter(sub => 
        sub.subscription_customers.some(sc => 
          sc.customer_role.toLowerCase().includes(relationshipFilter.toLowerCase())
        )
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.start_date).getTime();
      const dateB = new Date(b.start_date).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    setFilteredSubscriptions(filtered);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCustomerFilter('');
    setRelationshipFilter('');
    setShowFilters(false);
  };

  const handleSelectSubscription = (subscription: SubscriptionWithRelations) => {
    console.log('🎯 STEP2: Selecionando assinatura:', subscription);
    console.log('🎯 STEP2: Current location:', window.location.pathname);
    
    // Verificar se existe um cliente proprietário (owner) na assinatura
    const ownerCustomer = subscription.subscription_customers.find(sc => sc.customer_role === 'owner');
    if (!ownerCustomer) {
      console.error('🚨 STEP2: Assinatura sem cliente proprietário:', subscription);
      toast.error('Esta assinatura não possui um cliente proprietário definido.');
      return;
    }
    
    setState(prev => ({ ...prev, selectedSubscription: subscription }));
    navigate('/class-wizard/step3');
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <ClassWizardLayout
      currentStep={2}
      title="Selecionar Assinatura"
      description={`Selecione uma assinatura ativa para o cliente ${state.selectedCustomer?.name || ''}`}
      nextDisabled={true}
      showBackButton={true}
    >
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Assinaturas Ativas</p>
                {loading ? (
                  <Skeleton className="h-7 w-16 mt-1" />
                ) : (
                  <p className="text-2xl font-extrabold">{subscriptions.length}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Disponíveis</p>
                {loading ? (
                  <Skeleton className="h-7 w-16 mt-1" />
                ) : (
                  <p className="text-2xl font-extrabold">{filteredSubscriptions.length}</p>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selecionada</p>
                {loading ? (
                  <Skeleton className="h-7 w-16 mt-1" />
                ) : (
                  <p className="text-2xl font-extrabold">{state.selectedSubscription ? 1 : 0}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Info */}
        {state.selectedCustomer && (
          <Card className="bg-muted/30 mb-4">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-sm font-extrabold text-muted-foreground">Cliente Selecionado</h3>
                  <p className="text-base font-extrabold">{state.selectedCustomer.name}</p>
                  <p className="text-sm text-muted-foreground">{state.selectedCustomer.legal_name || ''}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/class-wizard/step1')}
                >
                  Alterar Cliente
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters and Search */}
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar assinaturas..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleFilters}
                className={cn(showFilters && 'bg-primary/10')}
                title="Mostrar filtros"
              >
                <Filter className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleSortOrder}
                title={`Ordenar por data ${sortOrder === 'asc' ? 'crescente' : 'decrescente'}`}
              >
                {sortOrder === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
              </Button>
              {(searchTerm || customerFilter || relationshipFilter) && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={resetFilters}
                  title="Limpar filtros"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="outline"
                onClick={fetchSubscriptions}
                disabled={loading}
                className="whitespace-nowrap"
              >
                Atualizar
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/20 rounded-lg border animate-in fade-in-0 slide-in-from-top-5">
              <div>
                <label htmlFor="customerFilter" className="text-sm font-extrabold">
                  Filtrar por Cliente
                </label>
                <Input
                  id="customerFilter"
                  placeholder="Nome do cliente"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor="relationshipFilter" className="text-sm font-extrabold">
                  Filtrar por Relacionamento
                </label>
                <Input
                  id="relationshipFilter"
                  placeholder="Tipo de relacionamento"
                  value={relationshipFilter}
                  onChange={(e) => setRelationshipFilter(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </div>

        {/* Subscriptions Table */}
        <Card className="overflow-hidden border-primary/10">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                              <TableRow className="hover:bg-transparent">
                <TableHead>ID da Assinatura</TableHead>
                <TableHead>Hierarquia</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Término</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array(3).fill(0).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-9 w-20 ml-auto" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <p className="mb-2">Nenhuma assinatura encontrada</p>
                        <Button variant="outline" size="sm" onClick={resetFilters}>Limpar filtros</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <TableRow 
                      key={subscription.id}
                      className={cn(
                        "cursor-pointer transition-colors",
                        state.selectedSubscription?.id === subscription.id ? 'bg-primary/5' : 'hover:bg-muted/50'
                      )}
                      onClick={() => handleSelectSubscription(subscription)}
                    >
                      <TableCell className="font-mono text-sm">
                        {subscription.display_id || `#${subscription.id.substring(0, 8)}`}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            subscription.subscription_type === 'main'
                              ? "bg-blue-50 text-blue-700 hover:bg-blue-50 border-blue-200"
                              : "bg-purple-50 text-purple-700 hover:bg-purple-50 border-purple-200"
                          )}
                        >
                          {subscription.subscription_type === 'main' ? 'Principal' : 'Filial'}
                        </Badge>
                        {subscription.subscription_type === 'child' && subscription.parent_subscription && (
                          <div className="text-xs text-blue-600 font-extrabold flex items-center gap-1 mt-1">
                            <span>↳ {subscription.parent_subscription.display_id}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(subscription.start_date)}</TableCell>
                      <TableCell>{formatDate(subscription.end_date)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant={state.selectedSubscription?.id === subscription.id ? "default" : "outline"}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectSubscription(subscription);
                          }}
                        >
                          {state.selectedSubscription?.id === subscription.id ? (
                            <>
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Selecionada
                            </>
                          ) : (
                            "Selecionar"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ClassWizardLayout>
  );
};

export default Step2Subscription; 